/**
 * AI-Integrated Competitor Intelligence — Epic D of Business Development.
 *
 * Single-page experience with:
 *   • Dashboard strip (total competitors, by threat level, AI-enriched %, open alerts)
 *   • Alert feed at top (high-severity competitor wins in our geo/industry)
 *   • Competitor list with inline "Enrich with AI" + "Run Scan" buttons
 *   • Detail drawer with AI profile, news feed, recent scans
 *
 * AI is powered by Ellora AI. The weekly
 * background scan is wired in server.py (Mon 03:00 UTC).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { apiGet, apiPost, apiPut, apiDel, apiPatch } from '../../lib/api';
import { formatDate as _fmtD } from '../../lib/format';
import { Plus, Sparkles, RefreshCw, Trash2, Edit2, X, Building2, MapPin, Loader2, AlertTriangle, ExternalLink, Search, Bell, Globe, BarChart3, ShieldAlert, ChevronRight, Newspaper } from 'lucide-react';
import { toast } from 'sonner';

const THREAT_LEVELS = ['Low', 'Medium', 'High'];
const initialForm = {
  name: '', hq: '', website: '',
  strengths: '', geos: '', industries: '',
  typical_value_range: '', est_revenue_cr: '',
  threat_level: 'Medium', notes: '', active: true,
};

const threatColor = (t) => {
  if (t === 'High') return 'bg-red-100 text-red-700';
  if (t === 'Medium') return 'bg-amber-100 text-amber-700';
  return 'bg-emerald-100 text-emerald-700';
};

const newsTypeIcon = (t) => {
  if (t === 'tender_win' || t === 'project_announcement') return '🏗️';
  if (t === 'executive_move') return '👤';
  if (t === 'partnership') return '🤝';
  if (t === 'award') return '🏆';
  if (t === 'risk') return '⚠️';
  return '📰';
};

const csvToList = (s) => (s || '').split(',').map((x) => x.trim()).filter(Boolean);
const listToCsv = (l) => Array.isArray(l) ? l.join(', ') : (l || '');

export default function Competitors() {
  const [comps, setComps] = useState([]);
  const [dash, setDash] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [search, setSearch] = useState('');
  const [threatFilter, setThreatFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(null);   // id of competitor currently enriching/scanning
  const [busyAction, setBusyAction] = useState(null);   // 'enrich' | 'scan'
  const [showAlerts, setShowAlerts] = useState(false);

  const loadAll = async () => {
    try {
      const [cList, cDash, cAlerts] = await Promise.all([
        apiGet('/competitors?active_only=false'),
        apiGet('/competitors/dashboard'),
        apiGet('/competitors/alerts'),
      ]);
      setComps(cList.competitors || []);
      setDash(cDash);
      setAlerts(cAlerts.alerts || []);
    } catch (e) {
      console.error('Failed to load competitors:', e);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const loadDetail = async (id) => {
    setSelectedId(id);
    setDetail(null);
    try {
      const d = await apiGet(`/competitors/${id}`);
      setDetail(d);
    } catch (e) {
      toast.error('Failed to load competitor detail');
    }
  };

  // UX nicety — Escape key closes detail drawer and modal
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (showForm) { setShowForm(false); return; }
      if (selectedId) { setSelectedId(null); setDetail(null); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showForm, selectedId]);

  const filtered = useMemo(() => {
    let list = comps;
    if (threatFilter !== 'all') list = list.filter((c) => c.threat_level === threatFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((c) =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.hq || '').toLowerCase().includes(q) ||
        (c.notes || '').toLowerCase().includes(q) ||
        (c.industries || []).some((i) => i.toLowerCase().includes(q)) ||
        (c.geos || []).some((g) => g.toLowerCase().includes(q))
      );
    }
    return list;
  }, [comps, search, threatFilter]);

  const openCreate = () => {
    setEditing(null);
    setForm(initialForm);
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name || '', hq: c.hq || '', website: c.website || '',
      strengths: listToCsv(c.strengths), geos: listToCsv(c.geos), industries: listToCsv(c.industries),
      typical_value_range: c.typical_value_range || '',
      est_revenue_cr: c.est_revenue_cr || '',
      threat_level: c.threat_level || 'Medium', notes: c.notes || '',
      active: c.active !== false,
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name required'); return; }
    const payload = {
      name: form.name.trim(),
      hq: form.hq.trim() || null,
      website: form.website.trim() || null,
      strengths: csvToList(form.strengths),
      geos: csvToList(form.geos),
      industries: csvToList(form.industries),
      typical_value_range: form.typical_value_range.trim() || null,
      est_revenue_cr: form.est_revenue_cr ? Number(form.est_revenue_cr) : null,
      threat_level: form.threat_level,
      notes: form.notes.trim() || null,
      active: form.active,
    };
    try {
      if (editing) {
        await apiPut(`/competitors/${editing.id}`, payload);
        toast.success('Competitor updated');
      } else {
        await apiPost('/competitors', payload);
        toast.success('Competitor added');
      }
      setShowForm(false);
      setEditing(null);
      setForm(initialForm);
      loadAll();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Save failed');
    }
  };

  const handleEnrich = async (c) => {
    setBusy(c.id); setBusyAction('enrich');
    try {
      const updated = await apiPost(`/competitors/${c.id}/enrich`);
      toast.success(`AI profile ready for ${c.name}`);
      // Refresh data
      await loadAll();
      if (selectedId === c.id) setDetail(updated);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI enrichment failed');
    } finally {
      setBusy(null); setBusyAction(null);
    }
  };

  const handleScan = async (c) => {
    setBusy(c.id); setBusyAction('scan');
    try {
      const res = await apiPost(`/competitors/${c.id}/scan`);
      toast.success(`Scan complete · ${res.captured} news · ${res.alerts_created} alert${res.alerts_created === 1 ? '' : 's'} created`);
      await loadAll();
      if (selectedId === c.id) loadDetail(c.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI scan failed');
    } finally {
      setBusy(null); setBusyAction(null);
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Remove "${c.name}" from competitor tracking?`)) return;
    try {
      await apiDel(`/competitors/${c.id}`);
      toast.success('Competitor removed');
      if (selectedId === c.id) { setSelectedId(null); setDetail(null); }
      loadAll();
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const handleDismissAlert = async (alertId) => {
    try {
      await apiPatch(`/competitors/alerts/${alertId}/dismiss`);
      setAlerts((a) => a.filter((x) => x.id !== alertId));
      toast.success('Alert dismissed');
    } catch (e) {
      toast.error('Failed to dismiss');
    }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">Business Development · AI-Powered</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-3">
            Competitor Intelligence <Sparkles size={28} className="text-amber-500" />
          </h1>
          <p className="text-sm text-slate-600 mt-1">Track competitors with AI-enriched profiles and weekly background scans for tender wins, executive moves, and partnerships.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAlerts(!showAlerts)}
            className="btn-secondary flex items-center gap-2 relative"
            data-testid="comp-alerts-toggle">
            <Bell size={16} /> Alerts
            {alerts.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">{alerts.length}</span>
            )}
          </button>
          <button onClick={openCreate} className="btn-primary flex items-center gap-2" data-testid="comp-add-btn">
            <Plus size={16} /> Add Competitor
          </button>
        </div>
      </div>

      {/* Stats strip */}
      {dash && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5" data-testid="comp-stats">
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="text-[10px] uppercase tracking-wider font-display font-bold text-slate-500">Tracked Competitors</div>
            <div className="font-display text-2xl font-extrabold text-[#0F172A] mt-1">{dash.total}</div>
          </div>
          <div className="bg-white border border-red-200 rounded-sm p-4">
            <div className="text-[10px] uppercase tracking-wider font-display font-bold text-red-700">High Threat</div>
            <div className="font-display text-2xl font-extrabold text-red-700 mt-1">{dash.by_threat?.High || 0}</div>
          </div>
          <div className="bg-white border border-amber-200 rounded-sm p-4">
            <div className="text-[10px] uppercase tracking-wider font-display font-bold text-amber-700">AI Enriched</div>
            <div className="font-display text-2xl font-extrabold text-amber-700 mt-1">{dash.ai_enriched}<span className="text-sm text-slate-500"> / {dash.total}</span></div>
          </div>
          <div className="bg-white border border-indigo-200 rounded-sm p-4">
            <div className="text-[10px] uppercase tracking-wider font-display font-bold text-[#3b4872]">Open Alerts</div>
            <div className="font-display text-2xl font-extrabold text-[#3b4872] mt-1">{dash.open_alerts}</div>
          </div>
        </div>
      )}

      {/* Alerts banner */}
      {showAlerts && (
        <div className="mb-5 bg-amber-50 border border-amber-300 rounded-sm p-4" data-testid="comp-alerts-panel">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-bold text-amber-900 flex items-center gap-2"><ShieldAlert size={18} /> Open Competitor Alerts</div>
            <button onClick={() => setShowAlerts(false)} className="text-amber-700 hover:text-amber-900"><X size={16} /></button>
          </div>
          {alerts.length === 0 ? (
            <div className="text-sm text-amber-800">No open alerts. AI will surface alerts here when a tracked competitor wins a tender in your geo or industry.</div>
          ) : (
            <div className="space-y-2">
              {alerts.map((a) => (
                <div key={a.id} className="bg-white border border-amber-200 rounded-sm p-3 flex items-start gap-3" data-testid={`comp-alert-${a.id}`}>
                  <span className={`tag ${a.severity === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{a.severity?.toUpperCase()}</span>
                  <div className="flex-1">
                    <div className="font-display font-semibold text-sm text-[#0F172A]">{a.competitor_name} · {a.headline}</div>
                    <div className="text-xs text-slate-600 mt-1">{a.details}</div>
                    <div className="text-[11px] text-slate-500 mt-1">
                      {a.location && <><MapPin size={11} className="inline" /> {a.location} · </>}
                      {a.industry && <>{a.industry} · </>}
                      {a.value_cr && <>₹{a.value_cr} Cr · </>}
                      {a.source_url && <a href={a.source_url} target="_blank" rel="noopener noreferrer" className="text-[#3b4872] hover:underline"><ExternalLink size={10} className="inline" /> Source</a>}
                    </div>
                  </div>
                  <button onClick={() => handleDismissAlert(a.id)} className="text-amber-700 hover:text-amber-900 text-xs underline" data-testid={`comp-alert-dismiss-${a.id}`}>Dismiss</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Filter bar */}
      <div className="mb-4 flex flex-wrap gap-2 items-center">
        <button onClick={() => setThreatFilter('all')} className={`px-3 py-1.5 rounded-sm text-xs font-display font-bold uppercase tracking-wider border ${threatFilter === 'all' ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-700 border-slate-300 hover:border-[#3b4872]'}`}>All ({comps.length})</button>
        {THREAT_LEVELS.map((t) => (
          <button key={t} onClick={() => setThreatFilter(t)} className={`px-3 py-1.5 rounded-sm text-xs font-display font-bold uppercase tracking-wider border ${threatFilter === t ? 'bg-[#3b4872] text-white border-[#3b4872]' : 'bg-white text-slate-700 border-slate-300 hover:border-[#3b4872]'}`}>
            {t} ({comps.filter((c) => c.threat_level === t).length})
          </button>
        ))}
        <div className="ml-auto relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, HQ, industry, geo..."
            className="input pl-9 text-sm w-80"
            data-testid="comp-search"
          />
        </div>
      </div>

      {/* Competitor list */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden" data-testid="comp-list">
        <table className="w-full dense-table">
          <thead>
            <tr>
              <th>Competitor</th>
              <th>HQ</th>
              <th>Industries / Geos</th>
              <th>Threat</th>
              <th>Last Scan</th>
              <th>News</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => loadDetail(c.id)} data-testid={`comp-row-${c.id}`}>
                <td>
                  <div className="font-display font-semibold text-[#0F172A] flex items-center gap-2">
                    <Building2 size={14} className="text-slate-400" />{c.name}
                  </div>
                  {c.website && (
                    <a href={c.website} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-[11px] text-[#3b4872] hover:underline flex items-center gap-1">
                      <Globe size={10} /> {c.website.replace(/^https?:\/\//, '').split('/')[0]}
                    </a>
                  )}
                </td>
                <td className="text-xs">{c.hq || '—'}</td>
                <td className="text-xs">
                  <div>{(c.industries || []).slice(0, 3).join(', ') || '—'}</div>
                  <div className="text-slate-500">{(c.geos || []).slice(0, 3).join(', ')}</div>
                </td>
                <td><span className={`tag ${threatColor(c.threat_level)}`}>{c.threat_level}</span></td>
                <td className="text-xs">{c.last_scan_at ? _fmtD(c.last_scan_at) : <span className="text-slate-400">Never</span>}</td>
                <td className="text-xs text-center font-display font-bold">{c.scan_count || 0}</td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleEnrich(c)} disabled={busy === c.id}
                      className="text-amber-600 hover:text-amber-700 px-1 disabled:opacity-50" title="AI Enrich Profile"
                      data-testid={`comp-enrich-${c.id}`}>
                      {busy === c.id && busyAction === 'enrich' ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                    </button>
                    <button
                      onClick={() => handleScan(c)} disabled={busy === c.id}
                      className="text-[#3b4872] hover:text-[#2e3a5d] px-1 disabled:opacity-50" title="AI Scan News"
                      data-testid={`comp-scan-${c.id}`}>
                      {busy === c.id && busyAction === 'scan' ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                    </button>
                    <button onClick={() => openEdit(c)} className="text-slate-500 hover:text-[#3b4872] px-1" title="Edit"><Edit2 size={14} /></button>
                    <button onClick={() => handleDelete(c)} className="text-red-600 hover:text-red-700 px-1" title="Delete"><Trash2 size={14} /></button>
                    <ChevronRight size={14} className="text-slate-400" />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="p-10 text-center text-sm text-slate-500">
            {comps.length === 0 ? 'No competitors tracked yet. Click "Add Competitor" to start.' : 'No competitors match the current filter.'}
          </div>
        )}
      </div>

      {/* Detail Drawer */}
      {selectedId && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => { setSelectedId(null); setDetail(null); }} />
          <div className="relative w-full max-w-2xl bg-white shadow-2xl h-full overflow-y-auto" data-testid="comp-drawer">
            {!detail ? (
              <div className="p-10 text-center text-sm text-slate-500"><Loader2 size={24} className="inline animate-spin" /> Loading…</div>
            ) : (
              <div>
                <div className="sticky top-0 bg-white border-b border-slate-200 p-5 flex items-start justify-between z-10">
                  <div>
                    <div className="font-display text-2xl font-extrabold text-[#0F172A]">{detail.name}</div>
                    <div className="text-xs text-slate-500 mt-1 flex items-center gap-3">
                      {detail.hq && <span><MapPin size={11} className="inline" /> {detail.hq}</span>}
                      <span className={`tag ${threatColor(detail.threat_level)} text-[10px]`}>{detail.threat_level} Threat</span>
                      {detail.last_scan_at && <span>Last scan: {_fmtD(detail.last_scan_at)}</span>}
                    </div>
                  </div>
                  <button onClick={() => { setSelectedId(null); setDetail(null); }} className="text-slate-500 hover:text-slate-700"><X size={20} /></button>
                </div>

                <div className="p-5 space-y-5">
                  <div className="flex gap-2">
                    <button onClick={() => handleEnrich(detail)} disabled={busy === detail.id} className="btn-secondary text-xs flex items-center gap-2">
                      {busy === detail.id && busyAction === 'enrich' ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} Enrich Profile
                    </button>
                    <button onClick={() => handleScan(detail)} disabled={busy === detail.id} className="btn-secondary text-xs flex items-center gap-2">
                      {busy === detail.id && busyAction === 'scan' ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Scan Now
                    </button>
                  </div>

                  {detail.ai_profile ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
                      <div className="font-display font-bold text-sm text-amber-900 mb-2 flex items-center gap-2"><Sparkles size={14} /> AI Profile <span className="text-[10px] text-amber-700">· {_fmtD(detail.ai_profile_at)}</span></div>
                      <div className="text-sm text-slate-700 mb-3 italic">{detail.ai_profile.summary}</div>
                      <div className="grid grid-cols-2 gap-3 text-xs">
                        {detail.ai_profile.founded_year && <div><span className="text-slate-500">Founded:</span> {detail.ai_profile.founded_year}</div>}
                        {detail.ai_profile.est_revenue_cr && <div><span className="text-slate-500">Revenue:</span> ₹{detail.ai_profile.est_revenue_cr} Cr</div>}
                        {detail.ai_profile.employee_count && <div><span className="text-slate-500">Employees:</span> {detail.ai_profile.employee_count}</div>}
                        {detail.ai_profile.pricing_stance && <div><span className="text-slate-500">Pricing:</span> {detail.ai_profile.pricing_stance}</div>}
                      </div>
                      {detail.ai_profile.strengths?.length > 0 && (
                        <div className="mt-3">
                          <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700 mb-1">Strengths</div>
                          <ul className="list-disc list-inside text-xs space-y-0.5 text-slate-700">{detail.ai_profile.strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
                        </div>
                      )}
                      {detail.ai_profile.weaknesses?.length > 0 && (
                        <div className="mt-3">
                          <div className="text-[10px] uppercase tracking-wider font-bold text-red-700 mb-1">Weaknesses</div>
                          <ul className="list-disc list-inside text-xs space-y-0.5 text-slate-700">{detail.ai_profile.weaknesses.map((s, i) => <li key={i}>{s}</li>)}</ul>
                        </div>
                      )}
                      {detail.ai_profile.key_executives?.length > 0 && (
                        <div className="mt-3">
                          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-1">Key Executives</div>
                          <div className="space-y-1">
                            {detail.ai_profile.key_executives.map((e, i) => (
                              <div key={i} className="text-xs"><span className="font-semibold">{e.name}</span> · <span className="text-slate-500">{e.designation}</span></div>
                            ))}
                          </div>
                        </div>
                      )}
                      {detail.ai_profile.recent_marquee_projects?.length > 0 && (
                        <div className="mt-3">
                          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-600 mb-1">Marquee Projects</div>
                          <div className="space-y-1">
                            {detail.ai_profile.recent_marquee_projects.map((p, i) => (
                              <div key={i} className="text-xs bg-white border border-slate-200 rounded-sm p-2">
                                <div className="font-semibold">{p.name}</div>
                                <div className="text-slate-500">{p.client} · {p.location} · ₹{p.value_cr} Cr ({p.year})</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 text-center text-sm text-slate-600">
                      No AI profile yet. Click <span className="font-bold">Enrich Profile</span> above to fetch.
                    </div>
                  )}

                  {/* News feed */}
                  <div>
                    <div className="font-display font-bold text-sm text-[#0F172A] mb-2 flex items-center gap-2"><Newspaper size={14} /> Recent Activity</div>
                    {detail.news && detail.news.length > 0 ? (
                      <div className="space-y-2">
                        {detail.news.map((n) => (
                          <div key={n.id} className="bg-white border border-slate-200 rounded-sm p-3" data-testid={`comp-news-${n.id}`}>
                            <div className="flex items-start gap-2">
                              <span className="text-lg">{newsTypeIcon(n.type)}</span>
                              <div className="flex-1">
                                <div className="font-display font-semibold text-sm">{n.headline}</div>
                                <div className="text-xs text-slate-600 mt-0.5">{n.details}</div>
                                <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                                  {n.location && <span><MapPin size={10} className="inline" /> {n.location}</span>}
                                  {n.industry && <span>{n.industry}</span>}
                                  {n.value_cr && <span>₹{n.value_cr} Cr</span>}
                                  {n.date && <span>{n.date}</span>}
                                  <span className={`tag text-[9px] ${n.confidence === 'high' ? 'bg-emerald-100 text-emerald-700' : n.confidence === 'low' ? 'bg-slate-100 text-slate-600' : 'bg-amber-100 text-amber-700'}`}>{n.confidence}</span>
                                  {n.source_url && <a href={n.source_url} target="_blank" rel="noopener noreferrer" className="text-[#3b4872] hover:underline"><ExternalLink size={10} className="inline" /> Source</a>}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-sm text-slate-500 italic">No news captured yet. Click <span className="font-bold">Scan Now</span> to fetch latest.</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 p-6 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold">{editing ? 'Edit Competitor' : 'Add Competitor'}</h2>
              <button onClick={() => setShowForm(false)} className="text-slate-500 hover:text-slate-700"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="label">Competitor Name *</label>
                  <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" required data-testid="comp-form-name" placeholder="e.g. Tata Projects Ltd" />
                </div>
                <div>
                  <label className="label">HQ</label>
                  <input type="text" value={form.hq} onChange={(e) => setForm({ ...form, hq: e.target.value })} className="input" placeholder="e.g. Mumbai, Maharashtra" />
                </div>
                <div>
                  <label className="label">Website</label>
                  <input type="url" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="input" placeholder="https://..." />
                </div>
                <div>
                  <label className="label">Threat Level *</label>
                  <select value={form.threat_level} onChange={(e) => setForm({ ...form, threat_level: e.target.value })} className="input" data-testid="comp-form-threat">
                    {THREAT_LEVELS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label">Est. Revenue (₹ Cr)</label>
                  <input type="number" value={form.est_revenue_cr} onChange={(e) => setForm({ ...form, est_revenue_cr: e.target.value })} className="input" placeholder="e.g. 1500" />
                </div>
                <div className="col-span-2">
                  <label className="label">Industries <span className="text-[10px] text-slate-500">(comma-separated)</span></label>
                  <input type="text" value={form.industries} onChange={(e) => setForm({ ...form, industries: e.target.value })} className="input" placeholder="e.g. EPC, Civil, Industrial, Warehousing" />
                </div>
                <div className="col-span-2">
                  <label className="label">Geographies <span className="text-[10px] text-slate-500">(comma-separated)</span></label>
                  <input type="text" value={form.geos} onChange={(e) => setForm({ ...form, geos: e.target.value })} className="input" placeholder="e.g. Maharashtra, Gujarat, Karnataka" />
                </div>
                <div className="col-span-2">
                  <label className="label">Strengths <span className="text-[10px] text-slate-500">(comma-separated)</span></label>
                  <input type="text" value={form.strengths} onChange={(e) => setForm({ ...form, strengths: e.target.value })} className="input" placeholder="e.g. PEB, Heavy civil, Fast execution" />
                </div>
                <div className="col-span-2">
                  <label className="label">Typical Bid Value Range</label>
                  <input type="text" value={form.typical_value_range} onChange={(e) => setForm({ ...form, typical_value_range: e.target.value })} className="input" placeholder="e.g. 50-300 Cr" />
                </div>
                <div className="col-span-2">
                  <label className="label">Internal Notes</label>
                  <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input" rows={3} placeholder="Internal observations, past encounters, win/loss history..." />
                </div>
                <div className="col-span-2 flex items-center gap-2">
                  <input type="checkbox" id="comp-active" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-[#3b4872]" />
                  <label htmlFor="comp-active" className="text-sm">Active (include in weekly AI scans)</label>
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-xs text-blue-900">
                <Sparkles size={12} className="inline mr-1" />
                Tip: Just enter the name + threat level. Use <b>Enrich with AI</b> after saving to auto-fill the rest.
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary" data-testid="comp-form-submit">{editing ? 'Update' : 'Add'} Competitor</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
